import { create } from 'zustand';
import { getPresetByKey, LIST_PRESETS } from '@engine/scale/presets';

/** One note inside a scale pattern. `semitones` is the offset from the
 *  bass (in integer semitones); `cents` is a microtonal tweak. */
export interface ScaleNote {
  semitones: number;
  cents: number;
}

/** Per-string absolute override — overrides the derived note for that
 *  string. Stored as pitch-class + octave + cents so it's human-legible. */
export interface StringOverride {
  pitchClass: string; // "C", "C#", ... "B"
  octave: number;
  cents: number;
}

/** A complete tuning configuration the player can switch to at any time. */
export interface TuningSet {
  name: string;
  presetKey: string | null; // null = custom / edited
  scaleSize: number;
  scaleNotes: ScaleNote[];
  /** Root pitch class (e.g. "F") — the scale's tonal centre, shown as R.
   *  NOT the pitch of the lowest string (see lowestDegreeIdx/bassOctave). */
  bassPitchClass: string;
  /** Octave register of the *lowest string*. With lowestDegreeIdx=0 this
   *  is the root's register; otherwise it's where the chosen non-root
   *  scale degree sits. */
  bassOctave: number;
  bassCents: number;
  /** Which scale degree (0-based) is the physically lowest string.
   *  0 = root-is-lowest (default). Changing this shifts the strings
   *  without changing the root (R). */
  lowestDegreeIdx: number;
  stringCount: number;
  /** How wide (in cents) the scale pattern spans before repeating.
   *  Default 1200 = one octave. Set 2400 for an 8-in-2-octaves scale,
   *  1902 for the Bohlen-Pierce tritave, etc. */
  scalePeriod: number;
  /** Per-string absolute overrides keyed by stringIndex. */
  overrides: Record<number, StringOverride>;
}

interface TuningState {
  sets: TuningSet[]; // always length 4
  activeIndex: number;
  advancedMode: boolean;
  /** String indices currently selected for partial-switch operations. */
  selection: number[];

  setActive: (i: number) => void;
  /** Overwrite the active set with a suggested tuning (preset family +
   *  root + bass octave + lowestDegreeIdx). Used when a new score is
   *  loaded so the player lands on a scale that fits the piece. Clears
   *  per-string overrides on the active set. */
  applyToActive: (sug: {
    presetKey: string;
    bassPitchClass: string;
    bassOctave: number;
    lowestDegreeIdx: number;
  }) => void;
  /** Rotate to the next set (wraps around). Useful for a mid-performance
   *  hand-gesture shortcut so the player never needs the panel. */
  cycleSet: () => void;
  updateSet: (i: number, patch: Partial<TuningSet>) => void;
  applyPreset: (setIndex: number, presetKey: string) => void;
  setStringOverride: (setIndex: number, stringIdx: number, ov: StringOverride) => void;
  clearStringOverride: (setIndex: number, stringIdx: number) => void;
  toggleSelection: (stringIdx: number) => void;
  clearSelection: () => void;
  selectAll: () => void;
  /** Copy the currently-derived string tunings FROM `fromSet` INTO the
   *  active set's overrides, but only for the currently-selected strings.
   *  This is the "partial switch" primitive. */
  copyToSelection: (fromSet: number) => void;
  setAdvancedMode: (v: boolean) => void;
  resetSet: (setIndex: number, presetKey?: string) => void;
  /** Append a new set, duplicated from the currently active one (so the
   *  user can make a transposed / tweaked variant quickly). The new set
   *  becomes active. */
  addSet: () => void;
  /** Remove a set. No-op if there is only one set. Active index adjusts
   *  so the newly-active set is still valid. */
  removeSet: (i: number) => void;
  renameSet: (i: number, name: string) => void;
}

const DEFAULT_PRESETS: string[] = [
  'pentatonic-major',
  'vietnamese-nam',
  'japanese-hirajoshi',
  'chromatic',
];

/** Hand-tuned first set — traditional đàn tranh tuning: pentatonic
 *  major (Do Re Mi Sol La = C D E G A) with the LOWEST string at E3
 *  (lowestDegreeIdx = 2 = the E degree of the C-pentatonic). String
 *  sequence: E3 G3 A3 C4 D4 E4 G4 A4 C5 D5 E5 G5 A5 C6 D6 E6 G6 —
 *  17 strings spanning ~3 octaves, matching a common 17-string
 *  đàn tranh setup. */
function makeDanTranhDefault(name: string): TuningSet {
  const preset = getPresetByKey('pentatonic-major')!;
  return {
    name,
    presetKey: preset.key,
    scaleSize: preset.scaleSize,
    scaleNotes: preset.scaleNotes.map((n) => ({ ...n })),
    bassPitchClass: 'C',
    // bassOctave=3 + lowestDegreeIdx=2 (E above C) → string[0] = E3.
    bassOctave: 3,
    bassCents: 0,
    lowestDegreeIdx: 2,
    stringCount: 17,
    scalePeriod: preset.scalePeriod ?? 1200,
    overrides: {},
  };
}

/** Generate a unique "Set N" name, skipping any N already taken. */
function nextSetName(existing: readonly TuningSet[]): string {
  const used = new Set(existing.map((s) => s.name));
  for (let i = 1; i <= 999; i++) {
    const candidate = `Set ${i}`;
    if (!used.has(candidate)) return candidate;
  }
  return `Set ${existing.length + 1}`;
}

/** Bring one set into canonical shape: bass clamped to C1…C8, scaleNotes
 *  sorted ascending by total cents. Used both on every write and once on
 *  rehydration to fix state persisted under older (buggy) versions. */
function sanitizeSet(set: TuningSet): TuningSet {
  const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const bassMidi =
    (set.bassOctave + 1) * 12 +
    Math.max(0, NOTES.indexOf(set.bassPitchClass.toUpperCase()));
  const clamped = Math.max(24, Math.min(108, bassMidi));
  const pc = NOTES[((clamped % 12) + 12) % 12]!;
  const oct = Math.floor(clamped / 12) - 1;
  const sorted = [...set.scaleNotes].sort((a, b) => {
    const ac = a.semitones * 100 + a.cents;
    const bc = b.semitones * 100 + b.cents;
    return ac - bc;
  });
  // Root (degree 0) is definitionally 0¢ — the scale origin. If a
  // rehydrated set has drifted, force it back so R stays nailed to
  // 12 o'clock on the circle.
  if (sorted.length > 0) sorted[0] = { semitones: 0, cents: 0 };
  const size = sorted.length || 1;
  const rawIdx = set.lowestDegreeIdx ?? 0;
  const lowestIdx = Math.max(0, Math.min(size - 1, Math.floor(rawIdx)));
  return {
    ...set,
    bassPitchClass: pc,
    bassOctave: oct,
    scaleNotes: sorted,
    lowestDegreeIdx: lowestIdx,
  };
}

const NOTES_CHROMA = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/** Pick a bassOctave so the middle string of this tuning lands near
 *  MIDI 66 (≈ F#4, 370 Hz) — roughly the sweet-spot of human hearing so
 *  a brand-new scale sounds pleasant without the player having to know
 *  how registers work. */
export function idealBassOctaveFor(
  rootPitchClass: string,
  scaleSize: number,
  stringCount: number,
  periodCents = 1200,
): number {
  const TARGET_MIDDLE_MIDI = 66;
  const rootChroma = Math.max(0, NOTES_CHROMA.indexOf(rootPitchClass.toUpperCase()));
  const middleIdx = Math.floor(stringCount / 2);
  const periodSemi = Math.max(1, periodCents / 100);
  const middleRelSemi = (middleIdx / Math.max(1, scaleSize)) * periodSemi;
  const idealLowestMidi = TARGET_MIDDLE_MIDI - middleRelSemi;
  const raw = Math.round((idealLowestMidi - rootChroma) / 12) - 1;
  return Math.max(1, Math.min(6, raw));
}

function makeSetFromPreset(presetKey: string, name: string): TuningSet {
  const preset = getPresetByKey(presetKey) ?? LIST_PRESETS()[0]!;
  const stringCount = 17;
  const hasPin = preset.defaultLowestDegreeIdx !== undefined;
  return {
    name,
    presetKey: preset.key,
    scaleSize: preset.scaleSize,
    scaleNotes: preset.scaleNotes.map((n) => ({ ...n })),
    bassPitchClass: preset.defaultBass.pitchClass,
    bassOctave: hasPin
      ? preset.defaultBass.octave
      : idealBassOctaveFor(
          preset.defaultBass.pitchClass,
          preset.scaleSize,
          stringCount,
        ),
    bassCents: preset.defaultBassCents ?? 0,
    lowestDegreeIdx: preset.defaultLowestDegreeIdx ?? 0,
    stringCount,
    scalePeriod: preset.scalePeriod ?? 1200,
    overrides: {},
  };
}

// Tuning state is intentionally NOT persisted — the user wants a fresh
// set of defaults on every session so experiments don't leak between
// sessions and the app always opens in a predictable state.
export const useTuningStore = create<TuningState>()(
    (set) => ({
      sets: DEFAULT_PRESETS.map((k, i) =>
        i === 0
          ? makeDanTranhDefault(`Set ${i + 1}`)
          : makeSetFromPreset(k, `Set ${i + 1}`),
      ),
      activeIndex: 0,
      advancedMode: false,
      selection: [],

      setActive: (i) => set({ activeIndex: i, selection: [] }),
      applyToActive: (sug) =>
        set((s) => {
          const preset = getPresetByKey(sug.presetKey);
          if (!preset) return s;
          const sets = s.sets.slice();
          const cur = sets[s.activeIndex]!;
          const size = Math.max(1, preset.scaleSize);
          const lowestIdx = Math.max(
            0,
            Math.min(size - 1, Math.floor(sug.lowestDegreeIdx)),
          );
          sets[s.activeIndex] = sanitizeSet({
            ...cur,
            presetKey: preset.key,
            scaleSize: preset.scaleSize,
            scaleNotes: preset.scaleNotes.map((n) => ({ ...n })),
            bassPitchClass: sug.bassPitchClass,
            bassOctave: sug.bassOctave,
            bassCents: preset.defaultBassCents ?? 0,
            lowestDegreeIdx: lowestIdx,
            scalePeriod: preset.scalePeriod ?? 1200,
            overrides: {},
          });
          return { sets, selection: [] };
        }),
      cycleSet: () =>
        set((s) => ({
          activeIndex: (s.activeIndex + 1) % s.sets.length,
          selection: [],
        })),
      updateSet: (i, patch) =>
        set((s) => {
          const sets = s.sets.slice();
          let next: TuningSet = { ...sets[i]!, ...patch };
          if (
            patch.scaleSize !== undefined ||
            patch.scaleNotes !== undefined ||
            patch.bassPitchClass !== undefined ||
            patch.bassOctave !== undefined ||
            patch.bassCents !== undefined
          ) {
            next = { ...next, presetKey: null };
          }
          sets[i] = sanitizeSet(next);
          return { sets };
        }),
      applyPreset: (i, presetKey) =>
        set((s) => {
          const preset = getPresetByKey(presetKey);
          if (!preset) return s;
          const sets = s.sets.slice();
          const cur = sets[i]!;
          const hasPin = preset.defaultLowestDegreeIdx !== undefined;
          sets[i] = {
            ...cur,
            presetKey: preset.key,
            scaleSize: preset.scaleSize,
            scaleNotes: preset.scaleNotes.map((n) => ({ ...n })),
            bassPitchClass: preset.defaultBass.pitchClass,
            bassOctave: hasPin
              ? preset.defaultBass.octave
              : idealBassOctaveFor(
                  preset.defaultBass.pitchClass,
                  preset.scaleSize,
                  cur.stringCount,
                ),
            bassCents: preset.defaultBassCents ?? 0,
            lowestDegreeIdx: preset.defaultLowestDegreeIdx ?? 0,
            scalePeriod: preset.scalePeriod ?? 1200,
            overrides: {}, // user rule: changing preset wipes overrides
          };
          return { sets };
        }),
      setStringOverride: (i, idx, ov) =>
        set((s) => {
          const sets = s.sets.slice();
          const cur = sets[i]!;
          sets[i] = { ...cur, overrides: { ...cur.overrides, [idx]: ov }, presetKey: null };
          return { sets };
        }),
      clearStringOverride: (i, idx) =>
        set((s) => {
          const sets = s.sets.slice();
          const cur = sets[i]!;
          const next = { ...cur.overrides };
          delete next[idx];
          sets[i] = { ...cur, overrides: next };
          return { sets };
        }),
      toggleSelection: (idx) =>
        set((s) => ({
          selection: s.selection.includes(idx)
            ? s.selection.filter((x) => x !== idx)
            : [...s.selection, idx],
        })),
      clearSelection: () => set({ selection: [] }),
      selectAll: () =>
        set((s) => {
          const n = s.sets[s.activeIndex]!.stringCount;
          return { selection: Array.from({ length: n }, (_, i) => i) };
        }),
      copyToSelection: (fromSetIdx) =>
        set((s) => {
          if (fromSetIdx === s.activeIndex) return s;
          const from = s.sets[fromSetIdx];
          const active = s.sets[s.activeIndex];
          if (!from || !active) return s;
          // Compute the source set's string tunings and capture them as
          // absolute overrides on the selected indices of the active set.
          const sourceStrings = deriveStrings(from);
          const nextOverrides = { ...active.overrides };
          for (const idx of s.selection) {
            const src = sourceStrings[idx];
            if (!src) continue;
            nextOverrides[idx] = {
              pitchClass: src.pitchClass,
              octave: src.octave,
              cents: src.cents,
            };
          }
          const sets = s.sets.slice();
          sets[s.activeIndex] = {
            ...active,
            overrides: nextOverrides,
            presetKey: null,
          };
          return { sets };
        }),
      setAdvancedMode: (v) => set({ advancedMode: v }),
      resetSet: (i, presetKey) =>
        set((s) => {
          const key = presetKey ?? DEFAULT_PRESETS[i] ?? 'pentatonic-major';
          const sets = s.sets.slice();
          sets[i] = makeSetFromPreset(key, s.sets[i]!.name);
          return { sets };
        }),
      addSet: () =>
        set((s) => {
          const source = s.sets[s.activeIndex]!;
          const copy: TuningSet = {
            ...source,
            name: nextSetName(s.sets),
            scaleNotes: source.scaleNotes.map((n) => ({ ...n })),
            overrides: { ...source.overrides },
          };
          return { sets: [...s.sets, copy], activeIndex: s.sets.length, selection: [] };
        }),
      removeSet: (i) =>
        set((s) => {
          if (s.sets.length <= 1) return s;
          const sets = s.sets.filter((_, j) => j !== i);
          let activeIndex = s.activeIndex;
          if (i === activeIndex) activeIndex = Math.max(0, i - 1);
          else if (i < activeIndex) activeIndex = activeIndex - 1;
          return { sets, activeIndex, selection: [] };
        }),
      renameSet: (i, name) =>
        set((s) => {
          const sets = s.sets.slice();
          sets[i] = { ...sets[i]!, name: name.slice(0, 32) };
          return { sets };
        }),
    }),
);

// Clear any previously persisted tuning state from older builds.
if (typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem('adt3.tuning');
  } catch {
    /* ignore storage access errors */
  }
}

/**
 * Selector hook: the active tuning set. Re-renders only when the active
 * set's content changes.
 */
export const useActiveTuning = (): TuningSet =>
  useTuningStore((s) => s.sets[s.activeIndex]!);

// --- Derivation ------------------------------------------------------------

export interface DerivedString {
  stringIndex: number;
  pitchClass: string;
  octave: number;
  cents: number;
  midi: number; // integer-semitone MIDI (cents added separately at playback)
  freq: number; // Hz, including cents
  isRoot: boolean; // first note of the scale in this octave
  /** True for the scale degree whose interval from root is nearest to
   *  a Perfect 5th (700¢). For pentatonic/major/etc this is the G in a
   *  C scale, A in D, etc. Used alongside isRoot to highlight the two
   *  most stable "home" tones with the same accent colour, so players
   *  have two visual anchors instead of just the root. */
  isPerfectFifth: boolean;
  fromOverride: boolean;
}

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

function noteToMidi(pitchClass: string, octave: number): number {
  const idx = NOTE_NAMES.indexOf(pitchClass.toUpperCase());
  return (octave + 1) * 12 + (idx >= 0 ? idx : 0);
}
function midiToPitchClass(midi: number): { pitchClass: string; octave: number } {
  const octave = Math.floor(midi / 12) - 1;
  const pitchClass = NOTE_NAMES[((midi % 12) + 12) % 12]!;
  return { pitchClass, octave };
}
function midiToFreq(midi: number, cents = 0): number {
  return 440 * Math.pow(2, (midi - 69) / 12 + cents / 1200);
}

/** Turn a TuningSet into an ordered list of string tunings. Overrides
 *  take absolute priority — the derived value for a string is skipped
 *  entirely when an override exists. Scale cycles use `scalePeriod`
 *  (cents) so non-octave scales work: e.g., 5 notes with period=2400
 *  means string 5 is 2 octaves above string 0, not 1. */
export function deriveStrings(t: TuningSet): DerivedString[] {
  const period = t.scalePeriod > 0 ? t.scalePeriod : 1200;
  const size = Math.max(1, t.scaleSize);
  const lowestIdx = Math.max(0, Math.min(size - 1, t.lowestDegreeIdx ?? 0));
  const lowestNote = t.scaleNotes[lowestIdx] ?? { semitones: 0, cents: 0 };
  // Physical MIDI of string[0]: root pitch class bumped up by the
  // lowest-degree's semitone offset, placed at bassOctave.
  const rootIdx = NOTE_NAMES.indexOf(t.bassPitchClass.toUpperCase());
  const lowestChromaSemi = (rootIdx + lowestNote.semitones) % 12;
  const lowestExtraOct = Math.floor((rootIdx + lowestNote.semitones) / 12);
  const lowestMidi =
    (t.bassOctave + 1 + lowestExtraOct) * 12 + lowestChromaSemi;
  const lowestTotalCents = lowestNote.semitones * 100 + lowestNote.cents;
  // Find the scale degree whose total-cents-from-root is closest to
  // 700¢ (a Perfect 5th). For most diatonic / pentatonic scales this is
  // an exact match; for microtonal scales we pick the nearest degree so
  // there's always one visual "P5 anchor" per scale. Degree 0 (root)
  // is never eligible — the P5 flag is additive, not a duplicate.
  let p5Degree = -1;
  let p5Delta = Infinity;
  for (let d = 1; d < size; d++) {
    const n = t.scaleNotes[d] ?? { semitones: 0, cents: 0 };
    const totalCents = n.semitones * 100 + n.cents;
    const delta = Math.abs(totalCents - 700);
    if (delta < p5Delta) {
      p5Delta = delta;
      p5Degree = d;
    }
  }
  // Require the match to be within a semitone — if the nearest degree
  // is still more than 100¢ away (e.g. a whole-tone scale), there is
  // no sensible P5 in this scale and we leave p5Degree as -1.
  if (p5Delta > 100) p5Degree = -1;

  const out: DerivedString[] = [];
  for (let i = 0; i < t.stringCount; i++) {
    const ov = t.overrides[i];
    if (ov) {
      const midi = noteToMidi(ov.pitchClass, ov.octave);
      out.push({
        stringIndex: i,
        pitchClass: ov.pitchClass,
        octave: ov.octave,
        cents: ov.cents,
        midi,
        freq: midiToFreq(midi, ov.cents),
        isRoot: false,
        isPerfectFifth: false,
        fromOverride: true,
      });
      continue;
    }
    const rawIdx = lowestIdx + i;
    const degree = rawIdx % size;
    const cycleRel = Math.floor(rawIdx / size);
    const note = t.scaleNotes[degree] ?? { semitones: 0, cents: 0 };
    // Cents relative to string[0] = (this degree – lowest degree) + full cycles × period.
    const thisTotalCents = note.semitones * 100 + note.cents + cycleRel * period;
    const relFromLowest = thisTotalCents - lowestTotalCents;
    const absCents = lowestMidi * 100 + relFromLowest + t.bassCents;
    const midi = Math.round(absCents / 100);
    const cents = absCents - midi * 100;
    const { pitchClass, octave } = midiToPitchClass(midi);
    out.push({
      stringIndex: i,
      pitchClass,
      octave,
      cents,
      midi,
      freq: midiToFreq(midi, cents),
      isRoot: degree === 0,
      isPerfectFifth: degree === p5Degree,
      fromOverride: false,
    });
  }
  return out;
}
